import { Controller } from "@hotwired/stimulus"
import { namedParticipantIdsFromRows, syncSupplyExitContractorSelects } from "../helpers/assignment_participants.js"

// Connects to data-controller="field-task-log-modal"
export default class extends Controller {
  static targets = [
    "quantity",
    "laborUnits",
    "laborRate",
    "totalCost",
    "progressHint",
    "participantCheckbox",
    "participantLaborUnits",
    "participantLaborRate",
    "participantMassLaborUnits",
    "otherParticipantsToggle",
    "supplyExitContractor",
  ]
  static values = {
    costMethod: { type: String, default: "task_unit" },
    collapsedGroupJornal: { type: Boolean, default: false },
    currency: String,
    currencyDecimalPlaces: { type: Number, default: 2 },
    currencyFormat: String,
    currencySubunitToUnit: { type: Number, default: 100 },
    currencySymbol: String,
    decimalSeparator: String,
    delimiter: String,
    locale: String,
    laborDayEquivalent: { type: Boolean, default: false },
    laborRateCents: { type: Number, default: 0 },
    openAssignment: { type: Boolean, default: false },
    unitPriceCents: Number,
  }

  connect() {
    this.#mirrorQuantityIfLaborDay()
    this.#syncParticipantRows()
    this.#syncSupplyExitContractors()
    this.updateTotalCost()
    this.updateProgressHint()
  }

  mirrorQuantityToLaborUnits() {
    this.#mirrorQuantityIfLaborDay()
    this.updateTotalCost()
  }

  #mirrorQuantityIfLaborDay() {
    if (!this.laborDayEquivalentValue) return
    if (this.costMethodValue !== "labor_unit") return
    if (!this.hasQuantityTarget || !this.hasLaborUnitsTarget) return
    this.laborUnitsTarget.value = this.quantityTarget.value
  }

  updateTotalCost() {
    if (this.hasTotalCostTarget) {
      const cents = this.totalCostCents()
      if (cents === null) {
        this.totalCostTarget.textContent = "—"
        if (this.hasLaborRateTarget) this.laborRateTarget.classList.add("input-error")
      } else {
        if (this.hasLaborRateTarget) this.laborRateTarget.classList.remove("input-error")
        this.totalCostTarget.textContent = this.formatMoney(cents)
      }
    }

    this.updateProgressHint()
  }

  updateProgressHint() {
    if (!this.collapsedGroupJornalValue) return
    if (!this.hasProgressHintTarget) return

    const scheduled = Number(this.progressHintTarget.dataset.scheduled || 0)
    const recordedBase = Number(this.progressHintTarget.dataset.recordedBase || 0)
    const thisLogUnits = this.participantLaborUnitsTotal()
    const recorded = recordedBase + thisLogUnits
    const remaining = Math.max(scheduled - recorded, 0)
    const template = this.progressHintTarget.dataset.hintTemplate || ""

    this.progressHintTarget.textContent = template
      .replace("__SCHEDULED__", this.formatQuantityWithUnit(scheduled))
      .replace("__RECORDED__", this.formatQuantityWithUnit(recorded))
      .replace("__REMAINING__", this.formatQuantityWithUnit(remaining))
  }

  syncParticipantRowState(event) {
    const row = event.currentTarget?.closest("[data-participant-row]")
    if (row) this.#syncParticipantRow(row)
    this.#syncSupplyExitContractors()
    this.updateTotalCost()
  }

  totalCostCents() {
    if (this.costMethodValue === "labor_unit") {
      if (this.participantRows().length > 0) return this.participantTotalCostCents()

      const rate = this.laborRateCents()
      if (rate === null) return null
      return Math.round(this.laborUnits() * rate)
    }

    return Math.round(this.quantity() * this.unitPriceCentsValue)
  }

  quantity() {
    if (!this.hasQuantityTarget) return 0

    const value = this.quantityTarget.value.trim()
    if (value === "") return 0

    const quantity = Number(value)
    return Number.isFinite(quantity) ? quantity : 0
  }

  laborUnits() {
    if (!this.hasLaborUnitsTarget) return 0

    const value = this.laborUnitsTarget.value.trim()
    if (value === "") return 0

    const quantity = Number(value)
    return Number.isFinite(quantity) ? quantity : 0
  }

  laborRateCents() {
    if (!this.hasLaborRateTarget) return this.laborRateCentsValue

    const value = this.laborRateTarget.value.trim()
    if (value === "") return this.laborRateCentsValue

    const normalizedValue = this.normalizeMoneyValue(value)
    const rate = Number(normalizedValue)
    if (!Number.isFinite(rate)) return null

    return Math.round(rate * this.currencySubunitToUnit())
  }

  participantTotalCostCents() {
    let total = 0

    for (const row of this.participantRows()) {
      const checkbox = row.querySelector("input[type='checkbox']")
      if (!checkbox?.checked) continue

      const laborUnitsInput = row.querySelector("[data-field-task-log-modal-target~='participantLaborUnits']")
      const laborRateInput = row.querySelector("[data-field-task-log-modal-target~='participantLaborRate']")
      const laborUnits = this.numberFromInput(laborUnitsInput)
      const laborRate = this.moneyCentsFromInput(laborRateInput)
      if (laborRate === null) return null

      total += laborUnits * laborRate
    }

    return Math.round(total)
  }

  participantLaborUnitsTotal() {
    let total = 0

    for (const row of this.participantRows()) {
      const checkbox = row.querySelector("input[type='checkbox']")
      if (!checkbox?.checked) continue

      const laborUnitsInput = row.querySelector("[data-field-task-log-modal-target~='participantLaborUnits']")
      total += this.numberFromInput(laborUnitsInput)
    }

    return total
  }

  numberFromInput(input) {
    if (!input) return 0

    const value = input.value.trim()
    if (value === "") return 0

    const quantity = Number(value)
    return Number.isFinite(quantity) ? quantity : 0
  }

  moneyCentsFromInput(input) {
    if (!input) return 0

    const value = input.value.trim()
    if (value === "") return 0

    const normalizedValue = this.normalizeMoneyValue(value)
    const amount = Number(normalizedValue)
    if (!Number.isFinite(amount)) return null

    return Math.round(amount * this.currencySubunitToUnit())
  }

  applyParticipantLaborUnits() {
    if (!this.hasParticipantMassLaborUnitsTarget) return

    const value = this.participantMassLaborUnitsTarget.value.trim()
    if (value === "") return

    this.participantRows().forEach((row) => {
      const checkbox = row.querySelector("input[type='checkbox']")
      if (!checkbox?.checked) return

      const laborUnitsInput = row.querySelector("[data-field-task-log-modal-target~='participantLaborUnits']")
      if (laborUnitsInput) laborUnitsInput.value = value
    })

    this.updateTotalCost()
  }

  toggleOtherParticipants() {
    const section = this.element.querySelector("[data-other-participants-section]")
    if (!section) return

    const isOpening = section.classList.contains("hidden")
    section.classList.toggle("hidden", !isOpening)

    if (this.hasOtherParticipantsToggleTarget) {
      const label = isOpening
        ? this.otherParticipantsToggleTarget.dataset.hideLabel
        : this.otherParticipantsToggleTarget.dataset.showLabel
      if (label) this.otherParticipantsToggleTarget.textContent = label
    }
  }

  participantRows() {
    return Array.from(this.element.querySelectorAll("[data-participant-row]"))
  }

  #syncParticipantRows() {
    this.participantRows().forEach((row) => this.#syncParticipantRow(row))
  }

  #syncParticipantRow(row) {
    const checkbox = row.querySelector("input[type='checkbox']")
    const checked = checkbox?.checked === true
    const laborUnitsInput = row.querySelector("[data-field-task-log-modal-target~='participantLaborUnits']")
    const laborRateInput = row.querySelector("[data-field-task-log-modal-target~='participantLaborRate']")

    if (laborUnitsInput) {
      laborUnitsInput.disabled = !checked
      if (!checked) {
        laborUnitsInput.value = this.openAssignmentValue ? "1" : "0"
      } else if (this.openAssignmentValue && this.numberFromInput(laborUnitsInput) === 0) {
        laborUnitsInput.value = "1"
      }
    }

    if (laborRateInput) laborRateInput.disabled = !checked
  }

  #syncSupplyExitContractors() {
    if (!this.openAssignmentValue) return

    syncSupplyExitContractorSelects(
      this.supplyExitContractorTargets,
      namedParticipantIdsFromRows(this.participantRows())
    )
  }

  normalizeMoneyValue(value) {
    let normalized = value.replace(/[^\d.,-]/g, "")
    const delimiter = this.delimiterValue
    const decimalSeparator = this.decimalSeparatorValue

    if (delimiter) {
      normalized = normalized.replace(new RegExp(this.escapeRegExp(delimiter), "g"), "")
    }

    if (decimalSeparator && decimalSeparator !== ".") {
      normalized = normalized.replace(new RegExp(this.escapeRegExp(decimalSeparator), "g"), ".")
    }

    return normalized
  }

  escapeRegExp(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  }

  formatQuantityWithUnit(quantity) {
    const unitOne = this.progressHintTarget.dataset.unitOne || ""
    const unitOther = this.progressHintTarget.dataset.unitOther || unitOne
    const unit = quantity === 1 ? unitOne : unitOther
    return `${this.formatQuantity(quantity)} ${unit}`.trim()
  }

  formatQuantity(quantity) {
    const value = Number(quantity)
    if (!Number.isFinite(value)) return "0"

    const fixed = value.toFixed(2).replace(/\.?0+$/, "")
    const [integerPart, decimalPart] = fixed.split(".")
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, this.delimiterValue)

    if (!decimalPart) return formattedInteger
    return `${formattedInteger}${this.decimalSeparatorValue}${decimalPart}`
  }

  formatMoney(cents) {
    const amount = cents / this.currencySubunitToUnit()
    const decimalPlaces = this.currencyDecimalPlaces()
    const sign = amount < 0 ? "-" : ""
    const [integerPart, decimalPart] = Math.abs(amount).toFixed(decimalPlaces).split(".")
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, this.delimiterValue)
    const formattedNumber = decimalPlaces > 0
      ? `${sign}${formattedInteger}${this.decimalSeparatorValue}${decimalPart}`
      : `${sign}${formattedInteger}`
    const format = this.currencyFormatValue || "%u%n"

    return format
      .replace("%u", this.currencySymbolValue || this.currencyValue)
      .replace("%n", formattedNumber)
  }

  currencySubunitToUnit() {
    return this.currencySubunitToUnitValue > 0 ? this.currencySubunitToUnitValue : 100
  }

  currencyDecimalPlaces() {
    return this.currencyDecimalPlacesValue >= 0 ? this.currencyDecimalPlacesValue : 2
  }
}
